/**
 * @fileOverview This file contains the implementation of the Navbar component used on the platform.
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

import React, { useContext, useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLanguage, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';

import { DarkModeContext } from '../../context/darkModeContext';
import {
  Box,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { useSetAtom } from 'jotai';
import { currentConfigTabAtom } from '../../stores/configTabsStore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  faMessageQuestion,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import ModalContact from '../Modal/ModalContact';
import companyLogo from '../../media/Logos/logo-horizontal.png';

import { useAtom } from 'jotai';
import {
  tableLocaleAtom,
  getLocaleFromLanguage,
} from '../../stores/tableLocaleAtom';
import i18next from 'i18next';
import { useUserPermissions } from '../../hooks/userPermissions';

const Navbar = () => {
  const { dispatch, darkMode } = useContext(DarkModeContext);
  const userPermissions = useUserPermissions();

  const setCurrentConfigTab = useSetAtom(currentConfigTabAtom);

  const [demoSnackbar, setDemoSnackbar] = useState(true);
  const [open, setOpen] = useState(false);
  const [openLangs, setOpenLangs] = useState(false);
  const anchorRef = useRef(null);
  const anchorRefLang = useRef(null);

  const { t, i18n } = useTranslation();

  const spanishString = t('navbar.spanish');
  const englishString = t('navbar.english');

  const logoutString = t('navbar.logout');
  const configString = t('navbar.settings');

  const name = localStorage.getItem('username');
  const first_surname = localStorage.getItem('first_surname');

  const [modalContactShow, setModalContactShow] = useState(false);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleLangToggle = () => {
    setOpenLangs((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleLangClose = (event) => {
    if (anchorRefLang.current && anchorRefLang.current.contains(event.target)) {
      return;
    }
    setOpenLangs(false);
  };

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem('token')
  );

  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.setItem('isAuthenticated', 'false');
    setIsAuthenticated(false);
    localStorage.removeItem('username');
    localStorage.removeItem('first_surname');
    localStorage.removeItem('second_surname');
    localStorage.removeItem('userIdForPerm');
    localStorage.removeItem('userIdForDVisits');
    localStorage.removeItem('currUserLogged');
    localStorage.removeItem('currUserEmail');
    navigate('/');
  };

  // This effect runs whenever isAuthenticated changes
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/nonauth');
    }
  }, [isAuthenticated, navigate]);

  const [, setTableLocale] = useAtom(tableLocaleAtom);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setTableLocale(getLocaleFromLanguage(lang));
  };

  useEffect(() => {
    // Set Spanish as default language on component mount
    if (!localStorage.getItem('defaultLanguageSet')) {
      // Wait for i18next to be ready before changing language
      i18next.on('initialized', () => {
        handleLanguageChange('es');
        localStorage.setItem('defaultLanguageSet', 'true');
      });
    }
  }, []);

  return (
    <Box
      className="navbar"
      display="flex"
      justifyContent="space-between"
      p={2}
      backgroundColor="#F5F6F8"
    >
      <img className="logo" src={companyLogo} alt="Company logo" />
      <Grid className="navbar-alert-container"></Grid>
      {/* ICONS */}
      <Box display="flex">
        <span>
          <IconButton
            onClick={() => navigate('/tutorials')}
            sx={{ display: 'none' }}
            disabled={true}
          >
            <FontAwesomeIcon icon={faCircleInfo} className="navbar-icons" />
          </IconButton>
        </span>
        {/* <span>
          <IconButton onClick={() => setModalContactShow(true)}>
            <FontAwesomeIcon
              icon={faMessageQuestion}
              className="navbar-icons"
            />
          </IconButton>
        </span>
        <ModalContact
          show={modalContactShow}
          onHide={() => setModalContactShow(false)}
        /> */}
        {/* <span>
            <IconButton
              onClick={() => dispatch({ type: 'TOGGLE' })}
              disabled={true}
            >
            {darkMode === false ? (
              <FontAwesomeIcon icon={faMoon} className="navbar-icons" />
            ) : (
              <FontAwesomeIcon icon={faSun} className="navbar-icons" />
            )}
          </IconButton>
        </span> */}
        <Stack>
          {/* <span>
            <IconButton
              onClick={handleLangToggle}
              ref={anchorRefLang}
              aria-controls={open ? 'lang-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
            >
              <FontAwesomeIcon icon={faLanguage} className="navbar-icons" />
            </IconButton>
          </span> */}
          <Popper
            open={openLangs}
            anchorEl={anchorRefLang.current}
            role={undefined}
            placement="bottom-start"
            transition
            disablePortal
            style={{ zIndex: 9999 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleLangClose}>
                    <MenuList
                      autoFocusItem={open}
                      id="lang-menu"
                      aria-labelledby="lang-button"
                      onKeyDown={handleListKeyDown}
                    >
                      <MenuItem
                        onClick={() => {
                          handleLanguageChange('es');
                          setOpenLangs(false);
                        }}
                      >
                        {spanishString}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleLanguageChange('en');
                          setOpenLangs(false);
                        }}
                      >
                        {englishString}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Stack>
        <Stack>
          <span>
            <IconButton
              ref={anchorRef}
              aria-controls={open ? 'composition-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              <Typography className="navbar-icons">
                {name + ' ' + first_surname}
              </Typography>
            </IconButton>
          </span>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            placement="bottom-start"
            transition
            disablePortal
            style={{ zIndex: 9999 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList
                      autoFocusItem={open}
                      id="composition-menu"
                      aria-labelledby="composition-button"
                      onKeyDown={handleListKeyDown}
                    >
                      {/* <MenuItem onClick={handleClose}>Perfil</MenuItem> */}
                      {userPermissions.includes('View Config') && (
                        <MenuItem
                          onClick={() => {
                            setCurrentConfigTab(0);
                            navigate('/config');
                          }}
                        >
                          {configString}
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => logoutUser()}>
                        {logoutString}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Stack>
      </Box>
    </Box>
  );
};

export default Navbar;
